"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { validateTicketCalculations } from "@/utils/ticketCalculations";
import { formatDate } from "@/utils/formatters";
import {
  fetchTickets,
  postTicket,
  putTicket,
  deleteTicket,
} from "@/lib/apiClient";

const cache = new Map();

export const useTickets = (
  endpoint,
  resourceName = "Tickets",
  currentAdmin = null,
  initialTickets = []
) => {
  const [data, setData] = useState(initialTickets);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchData = useCallback(
    async (force = false) => {
      const cacheKey = endpoint;
      if (!force && cache.has(cacheKey)) {
        setData(cache.get(cacheKey).data);
        setLoading(false);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        const result = await fetchTickets(
          endpoint,
          undefined,
          undefined,
          abortControllerRef.current.signal
        );
        const newData = result.data || [];
        setData(newData);
        cache.set(cacheKey, { data: newData, timestamp: Date.now() });
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          toast.error(err.message || `Failed to load ${resourceName}`);
        }
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [endpoint, resourceName]
  );

  // --- Create ---
  const postData = useCallback(
    async (newData) => {
      try {
        setLoading(true);
        setError(null);

        if (!currentAdmin) {
          throw new Error("Admin session not found");
        }

        const validation = validateTicketCalculations(newData);
        if (!validation.isValid) {
          throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
        }

        const formattedData = {
          ...newData,
          dateOfJourney: newData.dateOfJourney
            ? formatDate(newData.dateOfJourney, "default")
            : null,
          purchaseDate: newData.purchaseDate
            ? formatDate(newData.purchaseDate, "default")
            : null,
          dateOfNameSubmission: newData.dateOfNameSubmission
            ? formatDate(newData.dateOfNameSubmission, "default")
            : null,
          outstandingDate: newData.outstandingDate
            ? formatDate(newData.outstandingDate, "default")
            : null,
        };

        const payload = {
          ...formattedData,
          performedBy: currentAdmin.email,
          adminId: currentAdmin.id,
        };

        const result = await postTicket(endpoint, payload);
        cache.delete(endpoint);
        await fetchData(true);

        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, resourceName, fetchData, currentAdmin]
  );

  // --- Update ---
  const putData = useCallback(
    async (_id, updatedData) => {
      try {
        setLoading(true);
        setError(null);

        if (!currentAdmin) {
          throw new Error("Admin session not found");
        }

        const payload = {
          _id,
          ...updatedData,
          performedBy: currentAdmin.email,
          adminId: currentAdmin.id,
        };

        const result = await putTicket(_id, payload);
        cache.delete(endpoint);
        await fetchData(true);

        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, resourceName, fetchData, currentAdmin]
  );

  // --- Delete ---
  const deleteData = useCallback(
    async (_id, extraData = {}) => {
      try {
        setLoading(true);
        setError(null);

        if (!currentAdmin || !currentAdmin.email) {
          throw new Error("Admin session not found or invalid");
        }

        const payload = {
          _id,
          performedBy: currentAdmin.email,
          adminId: currentAdmin.id,
          ...extraData,
        };

        const result = await deleteTicket(_id, payload);
        cache.delete(endpoint);
        await fetchData(true);

        return result;
      } catch (err) {
        setError(err.message);
        toast.error(err.message || `Failed to delete ${resourceName}`);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, resourceName, fetchData, currentAdmin]
  );

  useEffect(() => {
    fetchData(false);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(true),
    postData,
    putData,
    deleteData,
  };
};
